
import React, { useState } from 'react';
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { useNavigate, Link } from 'react-router-dom';
import { UserPlus, Loader2 } from 'lucide-react';
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import UserJourney from '@/components/UserJourney';
import { mobileSupabase as supabase } from '@/integrations/supabase/mobile-client';
import { logger } from '@/utils/logger';

const formSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters" }),
  email: z.string().email({ message: "Please enter a valid email address" }),
  password: z.string().min(6, { message: "Password must be at least 6 characters" }),
  confirmPassword: z.string().min(6, { message: "Confirm password is required" }),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
});

const SignUp = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [showUserJourney, setShowUserJourney] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsLoading(true);
    setErrorMessage(null);
    
    try {
      logger.log("Signup attempt with:", { email: values.email, name: values.name });
      
      // Register the user with Supabase - using correct import
      const { data, error } = await supabase.auth.signUp({
        email: values.email,
        password: values.password,
        options: {
          data: {
            full_name: values.name,
          }
        }
      });
      
      logger.log("Signup response:", { data, error });
      
      if (error) {
        logger.error("Signup error details:", error);
        setErrorMessage(error.message || "Signup failed. Please try again.");
        toast({
          title: "Signup failed",
          description: error.message,
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }
      
      if (data.user) {
        logger.log("Signup successful, user:", data.user);
        // User is auto-logged in by Supabase, show onboarding
        setShowUserJourney(true);
      } else {
        // If email confirmation is required
        logger.log("Email verification required");
        toast({
          title: "Verification email sent",
          description: "Please check your email to verify your account.",
        });
        navigate('/login');
      }
    } catch (error: any) {
      logger.error("Signup error:", error);
      setErrorMessage(error?.message || "An unexpected error occurred");
      toast({
        title: "Signup failed",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUserJourneyComplete = () => {
    setShowUserJourney(false);
    // User is already logged in, check their setup status
    navigate('/setup-check');
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 overflow-y-auto">
      <div className="max-w-md w-full p-6 bg-card rounded-lg shadow-lg border">
        <div className="text-center mb-6">
        <UserPlus className="mx-auto h-10 w-10 text-primary" />
        <h1 className="text-2xl font-bold mt-2">Create an Account</h1>
        <p className="text-muted-foreground mt-1">Sign up to start tracking your kid's quotes</p>
      </div>

      {errorMessage && (
        <div className="mb-4 p-3 bg-destructive/10 text-destructive rounded border border-destructive">
          <p className="text-sm">{errorMessage}</p>
        </div>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Full Name</FormLabel>
                <FormControl>
                  <Input placeholder="John Doe" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input placeholder="you@example.com" {...field} type="email" autoComplete="email" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <Input type="password" placeholder="••••••••" {...field} autoComplete="new-password" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Confirm Password</FormLabel>
                <FormControl>
                  <Input type="password" placeholder="••••••••" {...field} autoComplete="new-password" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating account...
              </>
            ) : (
              "Create Account"
            )}
          </Button>
        </form>
      </Form>
      
      <div className="mt-6 text-center text-sm">
        <p className="text-muted-foreground">
          Already have an account?{" "}
          <Link to="/login" className="text-primary font-medium hover:underline">
            Sign in
          </Link>
        </p>
      </div>

        <UserJourney
          isOpen={showUserJourney}
          onClose={handleUserJourneyComplete}
        />
      </div>
    </div>
  );
};

export default SignUp;